import { flatten } from 'lodash';
import camelCase from 'lodash/camelCase';
import { AppData } from '../state/AppData';
import { Avatar, Page } from '../state/models';
import { IPair } from '../utils/getPairedImage';

export const usePageAvatars = (avatars: Array<Avatar>, pages: Array<Page>, appState: AppData) => {
  return (page: number) => {
    if (!pages[page] || !pages[page].avatars) {
      return [];
    }

    const _avatars = Object.keys(pages[page].avatars).map((avatar) => {
      return avatars.find((avt) => camelCase(avt.uid) === avatar);
    });

    const getPairedImage = (avatar: Avatar, genderId: number, pair1: IPair, pair2: IPair) => {
      return pages[page].avatars[camelCase(avatar.uid)].find((obj: any) => {
        if (!obj[pair1.name] || !obj[pair2.name]) {
          return null;
        }

        if (obj.genderId === 1) {
          return obj[pair1.name] === pair1.value && obj[pair2.name] === pair2.value;
        }

        return obj[pair1.name] === pair1.value && obj[pair2.name] === pair2.value && obj.genderId === genderId;
      });
    };

    const generateZIndex = (key: string, i: number) => {
      if (key === 'glasses' || key === 'birthday' || key === 'stuffed_animal') {
        return (i + 1) * 7;
      }

      if (key.includes('eye') || key.includes('hair')) {
        return (i + 1) * 5;
      }

      return (i + 1) * 4;
    };

    return flatten(
      _avatars.map((avatar: any, i: number) => {
        let avatarParams = avatar.defaultPreview;
        let { hairStyle, hairColor, eyeShape, eyeColor, beardStyle, beardColor, sex, sexRaceType } = avatarParams;
        let genderId: number;

        let hairImage: any;
        let eyeImage: any;
        let beardImage: any;
        let raceImage: any;

        if (avatar.params) {
          avatarParams = {
            ...avatarParams,
            ...avatar.params,
          };
        }

        hairStyle = avatarParams.hairStyle;
        hairColor = avatarParams.hairColor;

        eyeShape = avatarParams.eyeShape;
        eyeColor = avatarParams.eyeColor;

        beardStyle = avatarParams.beardStyle;
        beardColor = avatarParams.beardColor;

        sex = avatarParams.sex;
        sexRaceType = avatarParams.sexRaceType;

        if (sex) {
          genderId = sex === 'm' ? 2 : 3;
        } else {
          genderId = avatar.genderId;
        }

        if (hairStyle) {
          hairImage = getPairedImage(
            avatar,
            genderId,
            { name: 'hairColor', value: hairColor },
            { name: 'hairStyle', value: hairStyle },
          );
        }

        if (eyeShape) {
          eyeImage = getPairedImage(
            avatar,
            genderId,
            { name: 'eyeColor', value: eyeColor },
            { name: 'eyeShape', value: eyeShape },
          );
        }

        if (beardStyle) {
          beardImage = getPairedImage(
            avatar,
            genderId,
            { name: 'beardColor', value: beardColor },
            { name: 'beardStyle', value: beardStyle },
          );
        }

        if (sexRaceType) {
          raceImage = getPairedImage(
            avatar,
            genderId,
            { name: 'sex', value: sex },
            { name: 'sexRaceType', value: sexRaceType },
          );
        }

        const otherImages = Object.keys(avatarParams)
          .map((key) => {
            const image = pages[page].avatars[camelCase(avatar.uid)]
              .filter((imagePreview: any) => {
                if (!hairStyle) {
                  return !Object.keys(imagePreview).find(
                    (preview: any) => preview.includes('beard') || preview.includes('sexRaceType'),
                  );
                }

                return !Object.keys(imagePreview).find(
                  (preview: any) =>
                    preview.includes('beard') ||
                    preview.includes('hair') ||
                    preview.includes('eyeShape') ||
                    preview.includes('sexRaceType'),
                );
              })
              .find((obj: any) => {
                if (obj.genderId === 1) {
                  return obj[key] === avatarParams[key] && obj.genderId === 1;
                }

                return obj[key] === avatarParams[key] && obj.genderId === genderId;
              });

            if (!image) {
              return null;
            }

            return {
              src: image.image.smallUrl,
              zIndex: generateZIndex(key, i),
            };
          })
          .filter(Boolean);

        return [
          ...(hairImage
            ? [
                {
                  src: hairImage.image.smallUrl,
                  zIndex: 6 * (i + 1),
                },
              ]
            : []),
          ...(beardImage
            ? [
                {
                  src: beardImage.image.smallUrl,
                  zIndex: 6 * (i + 1),
                },
              ]
            : []),
          ...(eyeImage
            ? [
                {
                  src: eyeImage.image.smallUrl,
                  zIndex: 6 * (i + 1),
                },
              ]
            : []),
          ...(raceImage
            ? [
                {
                  src: raceImage.image.smallUrl,
                  zIndex: 4 * (i + 1),
                },
              ]
            : []),
          ...otherImages,
        ];
      }),
    );
  };
};
